import { useState, useEffect, useContext } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db, auth } from "../../utils/firebase";
import { UserContext } from "../../contexts/user.context";

const AdminSettings = () => {
  const { user } = useContext(UserContext);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("support");
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
    document.title = "Staff Management";

  const fetchAdmins = async () => {
    try {
      const q = query(collection(db, "admins"), orderBy("assignedAt", "desc"));
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAdmins(data);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const accessToken = await auth.currentUser?.getIdToken();
      const wpToken = JSON.parse(localStorage.getItem("user"))?.token;
      // console.log(wpToken)
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/admin/assign-role`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
        body: JSON.stringify({ email, role, wpToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to assign role.");
      }

      setMessage({ type: "success", text: data.message });
      setEmail("");
      setRole("support");
      fetchAdmins();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async (targetUid) => {
    if (!window.confirm("Are you sure you want to revoke admin access for this user?")) return;
    
    setActionLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const accessToken = await auth.currentUser?.getIdToken();
      const wpToken = JSON.parse(localStorage.getItem("user"))?.token;

      const response = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/admin/revoke-role`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        },
        body: JSON.stringify({ targetUid, wpToken }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to revoke role.");
      }

      setMessage({ type: "success", text: data.message });
      fetchAdmins();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
        <p className="text-gray-500 font-medium">Loading settings...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-1">
      <div className="max-w-5xl mx-auto space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Staff Management</h2>
          <p className="text-sm text-gray-500 mt-1">Control who has access to the admin dashboard.</p>
        </div>

        {message.text && (
          <div className={`p-4 text-sm rounded-lg ${message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Grant Access</h3>
          <form onSubmit={handleAddAdmin} className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label htmlFor="adminEmail" className="block text-sm font-medium text-gray-700 mb-1">User Email Address</label>
              <input
                id="adminEmail"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="staff@example.com"
                className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none transition"
              />
            </div>
            <div className="w-full md:w-64">
              <label htmlFor="adminRole" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                id="adminRole"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full p-2.5 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition"
              >
                <option value="support">Support (Payout Approvals)</option>
                <option value="super_admin">Super Admin (Full Access)</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={actionLoading}
              className="w-full md:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium disabled:opacity-50"
            >
              {actionLoading ? "Processing..." : "Add Staff"}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
            <h3 className="text-lg font-semibold text-gray-900">Active Admin Roster</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-white border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500 font-semibold">
                  <th className="px-6 py-4">User</th>
                  <th className="px-6 py-4">Role</th>
                  <th className="px-6 py-4">Granted On</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {admins.map((adminUser) => (
                  <tr key={adminUser.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {adminUser.email}
                      {adminUser.uid === user?.uid && (
                        <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-[10px] rounded-full">You</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${
                        adminUser.role === 'super_admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-green-50 text-green-700 border-green-200'
                      }`}>
                        {adminUser.role === 'super_admin' ? 'Super Admin' : 'Support'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500">
                      {adminUser.assignedAt ? adminUser.assignedAt.toDate().toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {adminUser.uid !== user?.uid && (
                        <button
                          disabled={actionLoading}
                          onClick={() => handleRevoke(adminUser.uid)}
                          className="text-red-600 hover:text-red-800 text-xs font-medium transition-colors disabled:opacity-50"
                        >
                          Revoke Access
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {admins.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                      No staff members found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;